import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Button, 
  Typography, 
  Card, 
  Row, 
  Col, 
  Space, 
  Alert, 
  Divider,
  Tag,
  Tooltip,
  Spin,
  message
} from 'antd';
import { 
  ExclamationCircleOutlined, 
  UserOutlined, 
  ClockCircleOutlined,
  MergeOutlined,
  SaveOutlined,
  CloseOutlined
} from '@ant-design/icons';
import moment from 'moment';

const { Title, Text, Paragraph } = Typography;

// Rich Text Display Component for read-only HTML content
const RichTextDisplay = ({ content, style = {} }) => {
  if (!content || content.trim() === '') {
    return <Text type="secondary">(Empty)</Text>;
  }

  // Clean and sanitize HTML content for display
  const cleanContent = content
    .replace(/<p><\/p>/g, '') // Remove empty paragraphs
    .trim();

  if (!cleanContent) {
    return <Text type="secondary">(Empty)</Text>;
  }

  return (
    <div
      className="rich-text-display"
      style={{
        ...style,
        lineHeight: '1.6',
        fontSize: '14px',
        color: 'rgba(0, 0, 0, 0.85)'
      }}
      dangerouslySetInnerHTML={{ __html: cleanContent }}
    />
  );
};

const IdtConflictResolutionModal = ({
  visible,
  onCancel,
  onResolve,
  conflictData,
  loading = false
}) => {
  const [resolutionChoices, setResolutionChoices] = useState({});
  const [resolving, setResolving] = useState(false);

  // Reset resolution choices when modal opens with new conflict data
  useEffect(() => {
    if (visible && conflictData?.conflicts) {
      const initialChoices = {};
      conflictData.conflicts.forEach(conflict => {
        initialChoices[conflict.fieldName] = 'client'; // Default to user's version
      });
      setResolutionChoices(initialChoices);
    }
  }, [visible, conflictData]);

  // Field display names mapping
  const fieldDisplayNames = {
    idtActiveConcerns: 'Active Concerns',
    idtCurrentStatus: 'Current Status',
    idtActiveNotes: 'Changes Since Last Review',
    idtSmartGoalsProgress: 'SMART Goals Progress',
    idtNutritionStatusClassification: 'Nutrition Status Classification',
    idtPhysicalActivityStatusClassification: 'Physical Activity Status Classification',
    idtRecoveryStatusClassification: 'Recovery Status Classification',
    idtEmotionStatusClassification: 'Emotion Status Classification',
    idtEnvironmentalStatusClassification: 'Environmental Status Classification',
    idtNutritionStatus: 'Nutrition Status',
    idtPhysicalActivityStatus: 'Physical Activity Status',
    idtRecoveryStatus: 'Recovery Status',
    idtEmotionStatus: 'Emotion Status',
    idtEnvironmentalStatus: 'Environmental Status',
    idtPharmacyRecommendations: 'Pharmacy Recommendations',
    idtMdRecommendations: 'MD Recommendations',
    idtAdditionalNotes: 'Additional Notes'
  };

  const handleResolutionChoice = (fieldName, choice) => {
    setResolutionChoices(prev => ({
      ...prev,
      [fieldName]: choice
    }));
  };

  const handleResolveConflicts = async () => {
    setResolving(true);
    try {
      // Build the resolved data based on user choices
      const resolvedData = {};
      
      conflictData.conflicts.forEach(conflict => {
        const choice = resolutionChoices[conflict.fieldName];
        if (choice === 'server') {
          resolvedData[conflict.fieldName] = conflict.serverValue;
        } else if (choice === 'client') {
          resolvedData[conflict.fieldName] = conflict.clientValue;
        } else if (choice === 'merge') {
          // For merge, we'll combine both values with a separator
          const serverValue = conflict.serverValue || '';
          const clientValue = conflict.clientValue || '';

          // Handle HTML content merging
          const serverHtml = serverValue.trim();
          const clientHtml = clientValue.trim();

          if (!serverHtml && !clientHtml) {
            resolvedData[conflict.fieldName] = '';
          } else if (!serverHtml) {
            resolvedData[conflict.fieldName] = clientHtml;
          } else if (!clientHtml) {
            resolvedData[conflict.fieldName] = serverHtml;
          } else {
            // Merge both with a visual separator
            resolvedData[conflict.fieldName] = `${serverHtml}<hr style="margin: 16px 0; border: 1px solid #d9d9d9;"><p><strong>--- Merged with user changes ---</strong></p>${clientHtml}`;
          }
        }
      });

      await onResolve(resolvedData, resolutionChoices);
      message.success('Conflicts resolved successfully!');
    } catch (error) {
      console.error('Error resolving conflicts:', error);
      message.error('Failed to resolve conflicts. Please try again.');
    } finally {
      setResolving(false);
    }
  };

  const handleForceOverwrite = async () => {
    setResolving(true);
    try {
      // Use all client values (force overwrite)
      const overwriteData = {};
      conflictData.conflicts.forEach(conflict => {
        overwriteData[conflict.fieldName] = conflict.clientValue;
      });

      await onResolve(overwriteData, null, true); // true indicates force overwrite
      message.success('Changes saved successfully (overwrite)!');
    } catch (error) {
      console.error('Error force overwriting:', error);
      message.error('Failed to save changes. Please try again.');
    } finally {
      setResolving(false);
    }
  };

  const renderConflictField = (conflict) => {
    const fieldName = conflict.fieldName;
    const displayName = fieldDisplayNames[fieldName] || fieldName;
    const choice = resolutionChoices[fieldName];

    return (
      <Card 
        key={fieldName}
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <Text strong>{displayName}</Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
        size="small"
      >
        <Row gutter={16}>
          {/* Server Version (Other User's Changes) */}
          <Col span={12}>
            <Card 
              size="small" 
              title={
                <Space>
                  <UserOutlined />
                  <Text>Other User's Version</Text>
                  {conflict.lastModifiedBy && (
                    <Tag color="blue" size="small">
                      {conflict.lastModifiedBy.name || 'Unknown User'}
                    </Tag>
                  )}
                </Space>
              }
              extra={
                conflict.lastModifiedAt && (
                  <Tooltip title={moment(conflict.lastModifiedAt).format('YYYY-MM-DD HH:mm:ss')}>
                    <Tag icon={<ClockCircleOutlined />} size="small">
                      {moment(conflict.lastModifiedAt).fromNow()}
                    </Tag>
                  </Tooltip>
                )
              }
              style={{ 
                border: choice === 'server' ? '2px solid #52c41a' : '1px solid #d9d9d9',
                backgroundColor: choice === 'server' ? '#f6ffed' : undefined
              }}
            >
              <div style={{ maxHeight: '150px', overflow: 'auto', padding: '8px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <RichTextDisplay
                  content={conflict.serverValue}
                  style={{ minHeight: '20px' }}
                />
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <Button 
                size="small" 
                type={choice === 'server' ? 'primary' : 'default'}
                onClick={() => handleResolutionChoice(fieldName, 'server')}
              >
                Use This Version
              </Button>
            </Card>
          </Col>

          {/* Client Version (User's Changes) */}
          <Col span={12}>
            <Card 
              size="small" 
              title={
                <Space>
                  <UserOutlined />
                  <Text>Your Version</Text>
                  <Tag color="green" size="small">You</Tag>
                </Space>
              }
              style={{ 
                border: choice === 'client' ? '2px solid #52c41a' : '1px solid #d9d9d9',
                backgroundColor: choice === 'client' ? '#f6ffed' : undefined
              }}
            >
              <div style={{ maxHeight: '150px', overflow: 'auto', padding: '8px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <RichTextDisplay
                  content={conflict.clientValue}
                  style={{ minHeight: '20px' }}
                />
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <Space>
                <Button 
                  size="small" 
                  type={choice === 'client' ? 'primary' : 'default'}
                  onClick={() => handleResolutionChoice(fieldName, 'client')}
                >
                  Use This Version
                </Button>
                <Button 
                  size="small" 
                  type={choice === 'merge' ? 'primary' : 'default'}
                  icon={<MergeOutlined />}
                  onClick={() => handleResolutionChoice(fieldName, 'merge')}
                >
                  Merge Both
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </Card>
    );
  };

  if (!conflictData) {
    return null;
  }

  return (
    <>
      <style>
        {`
          .rich-text-display p {
            margin-bottom: 8px;
          }
          .rich-text-display p:last-child {
            margin-bottom: 0;
          }
          .rich-text-display ul, .rich-text-display ol {
            margin: 8px 0;
            padding-left: 20px;
          }
          .rich-text-display li {
            margin-bottom: 4px;
          }
          .rich-text-display strong {
            font-weight: 600;
          }
          .rich-text-display em {
            font-style: italic;
          }
          .rich-text-display hr {
            margin: 12px 0;
            border: none;
            border-top: 1px solid #d9d9d9;
          }
        `}
      </style>
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
            <Title level={4} style={{ margin: 0 }}>
              Resolve IDT Note Conflicts
            </Title>
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        width={1200}
        footer={null}
        maskClosable={false}
        destroyOnClose
      >
      <Spin spinning={loading || resolving}>
        <Alert
          message="Conflict Detected"
          description={
            <div>
              <Paragraph>
                Another user has modified this IDT note while you were editing. 
                Please review the conflicting changes below and choose how to resolve each conflict.
              </Paragraph>
              <Space>
                <Text strong>Server Version:</Text>
                <Text>Version {conflictData.serverVersion}</Text>
                <Text strong>Your Version:</Text>
                <Text>Version {conflictData.clientVersion}</Text>
              </Space>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <div style={{ maxHeight: '60vh', overflow: 'auto', marginBottom: 16 }}>
          {conflictData.conflicts?.map(renderConflictField)}
        </div>

        <Divider />

        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary">
              {conflictData.conflicts?.length || 0} conflict(s) detected
            </Text>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<CloseOutlined />}
                onClick={onCancel}
                disabled={resolving}
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                danger
                onClick={handleForceOverwrite}
                loading={resolving}
                disabled={loading}
              >
                Force Overwrite
              </Button>
              <Button 
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleResolveConflicts}
                loading={resolving}
                disabled={loading}
              >
                Resolve & Save
              </Button>
            </Space>
          </Col>
        </Row>
      </Spin>
    </Modal>
    </>
  );
};

export default IdtConflictResolutionModal;
